import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";

const SORTS = [
  { value: "popular", label: "Most popular" },
  { value: "trending", label: "Trend score" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
];

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "All";
  const badge = params.get("badge") || "";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "popular";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: { category, badge, search, sort } })
      .then((r) => setProducts(r.data.products))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, badge, search, sort]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8" data-testid="shop-page">
      <p className="eyebrow mb-2">Catalog</p>
      <h1 className="font-heading text-3xl font-bold text-white sm:text-4xl">All products</h1>

      <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="shop-search-input"
            defaultValue={search}
            onKeyDown={(e) => e.key === "Enter" && update("search", e.target.value)}
            placeholder="Search products… (press Enter)"
            className="input-dark !pl-11"
          />
        </div>
        <select
          data-testid="shop-sort-select"
          value={sort}
          onChange={(e) => update("sort", e.target.value)}
          className="input-dark lg:w-56"
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value} className="bg-[#121216]">{s.label}</option>
          ))}
        </select>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="shop-category-filters">
        {["All", ...categories.map((c) => c.name)].map((c) => (
          <button
            key={c}
            data-testid={`filter-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => update("category", c === "All" ? "" : c)}
            className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${
              category === c
                ? "border-amber-400 bg-amber-400/15 text-amber-300"
                : "border-white/10 text-slate-400 hover:border-white/25 hover:text-white"
            }`}
          >
            {c}
          </button>
        ))}
        {badge && (
          <button
            data-testid="filter-clear-badge"
            onClick={() => update("badge", "")}
            className="rounded-full border border-sky-400/40 bg-sky-400/10 px-4 py-1.5 text-xs font-medium text-sky-300"
          >
            {badge} ✕
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
        </div>
      ) : products.length === 0 ? (
        <p className="py-24 text-center text-slate-500" data-testid="shop-empty">No products match your filters.</p>
      ) : (
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="shop-grid">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}
